import React, { useState, useEffect, useRef } from 'react';
import { Card, Button, Form, Alert, Badge, Row, Col, Modal, Spinner } from 'react-bootstrap';
import { FaCode, FaSave, FaPlay, FaUndo, FaDownload, FaUpload, FaCog, FaRobot, FaCheck, FaExclamationTriangle } from 'react-icons/fa';
import axios from 'axios';
import { API_URL } from '../config';

const SupervisorCodeEditor = ({ 
  onSave, 
  onTest, 
  onClose,
  initialCode = '',
  availableAgents = [],
  workflowType = 'automatic'
}) => {
  const [code, setCode] = useState(initialCode);
  const [originalCode, setOriginalCode] = useState(initialCode);
  const [errors, setErrors] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [isValidating, setIsValidating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showAgentPalette, setShowAgentPalette] = useState(true);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [searchAgent, setSearchAgent] = useState('');
  const [editorMode, setEditorMode] = useState('code'); // 'code', 'visual', 'hybrid'
  const editorRef = useRef(null);
  const monacoRef = useRef(null);

  // Initialize Monaco Editor
  useEffect(() => {
    const loadMonaco = async () => {
      try {
        // Load Monaco from CDN
        const script = document.createElement('script');
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/monaco-editor/0.34.1/min/vs/loader.min.js';
        script.onload = () => {
          window.require.config({ paths: { vs: 'https://cdnjs.cloudflare.com/ajax/libs/monaco-editor/0.34.1/min/vs' } });
          window.require(['vs/editor/editor.main'], () => {
            initializeEditor();
          });
        };
        document.head.appendChild(script);
      } catch (error) {
        console.error('Failed to load Monaco Editor:', error);
      }
    };

    if (!window.monaco) {
      loadMonaco();
    } else {
      initializeEditor();
    }
  }, []);

  const initializeEditor = () => {
    if (editorRef.current && window.monaco) {
      monacoRef.current = window.monaco.editor.create(editorRef.current, {
        value: code,
        language: 'python',
        theme: 'vs-dark',
        automaticLayout: true,
        minimap: { enabled: true },
        scrollBeyondLastLine: false,
        fontSize: 14,
        lineNumbers: 'on',
        roundedSelection: false,
        scrollbar: {
          vertical: 'visible',
          horizontal: 'visible'
        },
        folding: true,
        glyphMargin: true,
        wordWrap: 'on'
      });

      // Set up change listener
      monacoRef.current.onDidChangeModelContent(() => {
        const value = monacoRef.current.getValue();
        setCode(value);
        validateCode(value);
      });

      // Set up custom completions for agents
      window.monaco.languages.registerCompletionItemProvider('python', {
        provideCompletionItems: (model, position) => {
          const suggestions = availableAgents.map(agent => ({
            label: `get_${agent.name}_agent()`,
            kind: window.monaco.languages.CompletionItemKind.Function,
            documentation: agent.description || 'Agent function',
            insertText: `get_${agent.name}_agent()`,
            range: {
              startLineNumber: position.lineNumber,
              endLineNumber: position.lineNumber,
              startColumn: position.column,
              endColumn: position.column
            }
          }));

          return { suggestions };
        }
      });
    }
  };

  const validateCode = async (codeToValidate) => {
    setIsValidating(true);
    try {
      // Basic Python syntax validation
      const response = await axios.post(`${API_URL}/api/validate-supervisor-code`, {
        code: codeToValidate,
        workflow_type: workflowType
      });

      if (response.data.success) {
        setErrors(response.data.errors || []);
        setWarnings(response.data.warnings || []);
        
        // Update Monaco editor markers
        if (monacoRef.current && window.monaco) {
          const markers = [
            ...response.data.errors.map(error => ({
              startLineNumber: error.line || 1,
              startColumn: error.column || 1,
              endLineNumber: error.line || 1,
              endColumn: error.column || 1,
              message: error.message,
              severity: window.monaco.MarkerSeverity.Error
            })),
            ...response.data.warnings.map(warning => ({
              startLineNumber: warning.line || 1,
              startColumn: warning.column || 1,
              endLineNumber: warning.line || 1,
              endColumn: warning.column || 1,
              message: warning.message,
              severity: window.monaco.MarkerSeverity.Warning
            }))
          ];
          
          window.monaco.editor.setModelMarkers(monacoRef.current.getModel(), 'supervisor', markers);
        }
      }
    } catch (error) {
      console.error('Code validation failed:', error);
    } finally {
      setIsValidating(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(code);
      setOriginalCode(code);
    } catch (error) {
      console.error('Save failed:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleTest = async () => {
    try {
      await onTest(code);
    } catch (error) {
      console.error('Test failed:', error);
    }
  };

  const handleReset = () => {
    setCode(originalCode);
    if (monacoRef.current) {
      monacoRef.current.setValue(originalCode);
    }
  };

  const insertAgentCode = (agent) => {
    const agentImport = `from agents.${agent.name} import get_agent as get_${agent.name}_agent`;
    const agentInstantiation = `${agent.name}_agent = get_${agent.name}_agent()`;
    
    const newCode = code + '\n' + agentImport + '\n' + agentInstantiation;
    setCode(newCode);
    
    if (monacoRef.current) {
      monacoRef.current.setValue(newCode);
    }
  };

  const generateSupervisorTemplate = () => {
    const template = `from langchain_openai import ChatOpenAI
from langgraph_supervisor import create_supervisor

# Import your agents
${availableAgents.slice(0, 5).map(agent => 
  `from agents.${agent.name} import get_agent as get_${agent.name}_agent`
).join('\n')}

from tenacity import retry, wait_random_exponential, stop_after_attempt

@retry(wait=wait_random_exponential(min=1, max=60), stop=stop_after_attempt(6))
def get_chat_model():
    return ChatOpenAI(model="gpt-4o")

def get_supervisor():
    model = get_chat_model()

    # Instantiate agents
${availableAgents.slice(0, 5).map(agent => 
  `    ${agent.name}_agent = get_${agent.name}_agent()`
).join('\n')}

    # Compile the supervisor with the agents
    supervisor = create_supervisor(
        agents=[
${availableAgents.slice(0, 5).map(agent => 
  `            ${agent.name}_agent,`
).join('\n')}
        ],
        model=model
    )

    return supervisor
`;

    setCode(template);
    if (monacoRef.current) {
      monacoRef.current.setValue(template);
    }
  };

  const filteredAgents = availableAgents.filter(agent =>
    agent.name.toLowerCase().includes(searchAgent.toLowerCase()) ||
    (agent.description && agent.description.toLowerCase().includes(searchAgent.toLowerCase()))
  );

  return (
    <Modal show={true} onHide={onClose} size="xl" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>
          <FaCode className="me-2" />
          Supervisor Code Editor - {workflowType === 'automatic' ? 'Automatic Mode' : 'Manual Mode'}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ height: '70vh', padding: 0 }}>
        <style>{`
          .code-editor-container {
            display: flex;
            height: 100%;
          }
          
          .editor-main {
            flex: 1;
            display: flex;
            flex-direction: column;
          }
          
          .editor-toolbar {
            background: #f8f9fa;
            border-bottom: 1px solid #dee2e6;
            padding: 10px;
            display: flex;
            justify-content: between;
            align-items: center;
          }
          
          .monaco-editor-container {
            flex: 1;
            border: 1px solid #dee2e6;
          }
          
          .agent-palette {
            width: 300px;
            background: #f8f9fa;
            border-left: 1px solid #dee2e6;
            display: flex;
            flex-direction: column;
          }
          
          .agent-palette-header {
            padding: 15px;
            border-bottom: 1px solid #dee2e6;
            background: white;
          }
          
          .agent-palette-content {
            flex: 1;
            overflow-y: auto;
            padding: 10px;
          }
          
          .agent-item {
            background: white;
            border: 1px solid #dee2e6;
            border-radius: 5px;
            padding: 10px;
            margin-bottom: 10px;
            cursor: pointer;
            transition: all 0.2s;
          }
          
          .agent-item:hover {
            border-color: #007bff;
            transform: translateY(-1px);
            box-shadow: 0 2px 5px rgba(0,0,0,0.1);
          }
          
          .validation-panel {
            background: #fff;
            border-top: 1px solid #dee2e6;
            padding: 10px;
            max-height: 150px;
            overflow-y: auto;
          }
          
          .error-item {
            color: #dc3545;
            font-size: 0.875rem;
            margin-bottom: 5px;
          }
          
          .warning-item {
            color: #fd7e14;
            font-size: 0.875rem;
            margin-bottom: 5px;
          }
        `}</style>

        <div className="code-editor-container">
          <div className="editor-main">
            {/* Editor Toolbar */}
            <div className="editor-toolbar">
              <div className="d-flex align-items-center">
                <Form.Select 
                  size="sm" 
                  value={editorMode} 
                  onChange={(e) => setEditorMode(e.target.value)}
                  style={{ width: '150px' }}
                  className="me-3"
                >
                  <option value="code">Code Editor</option>
                  <option value="visual">Visual Builder</option>
                  <option value="hybrid">Hybrid Mode</option>
                </Form.Select>
                
                <Button variant="outline-secondary" size="sm" className="me-2" onClick={generateSupervisorTemplate}>
                  <FaCog className="me-1" />
                  Template
                </Button>
                
                <Button variant="outline-warning" size="sm" className="me-2" onClick={handleReset}>
                  <FaUndo className="me-1" />
                  Reset
                </Button>
                
                {isValidating && (
                  <div className="d-flex align-items-center text-muted">
                    <Spinner animation="border" size="sm" className="me-2" />
                    Validating...
                  </div>
                )}
              </div>
              
              <div className="d-flex align-items-center">
                <Badge bg={errors.length > 0 ? 'danger' : warnings.length > 0 ? 'warning' : 'success'} className="me-2">
                  {errors.length > 0 ? `${errors.length} Errors` : 
                   warnings.length > 0 ? `${warnings.length} Warnings` : 
                   'Valid'}
                </Badge>
                
                <Button variant="outline-secondary" size="sm" className="me-2" onClick={() => setShowAgentPalette(!showAgentPalette)}>
                  <FaRobot className="me-1" />
                  Agents
                </Button>
              </div>
            </div>

            {/* Monaco Editor */}
            <div className="monaco-editor-container" ref={editorRef}></div>

            {/* Validation Panel */}
            {(errors.length > 0 || warnings.length > 0) && (
              <div className="validation-panel">
                {errors.map((error, index) => (
                  <div key={`error-${index}`} className="error-item">
                    <FaExclamationTriangle className="me-2" />
                    Line {error.line || '?'}: {error.message}
                  </div>
                ))}
                {warnings.map((warning, index) => (
                  <div key={`warning-${index}`} className="warning-item">
                    <FaExclamationTriangle className="me-2" />
                    Line {warning.line || '?'}: {warning.message}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Agent Palette */}
          {showAgentPalette && (
            <div className="agent-palette">
              <div className="agent-palette-header">
                <h6 className="mb-2">Available Agents</h6>
                <Form.Control
                  size="sm"
                  type="text"
                  placeholder="Search agents..."
                  value={searchAgent}
                  onChange={(e) => setSearchAgent(e.target.value)}
                />
              </div>
              
              <div className="agent-palette-content">
                {filteredAgents.map((agent, index) => (
                  <div
                    key={index}
                    className="agent-item"
                    onClick={() => insertAgentCode(agent)}
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      <strong>{agent.display_name || agent.name}</strong>
                      <Badge bg={agent.agentType === 'mock' ? 'success' : 'primary'} className="agent-badge">
                        {agent.agentType || 'unknown'}
                      </Badge>
                    </div>
                    <small className="text-muted">{agent.description || 'No description'}</small>
                  </div>
                ))}
                
                {filteredAgents.length === 0 && (
                  <div className="text-center text-muted">
                    <p>No agents found</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </Modal.Body>

      <Modal.Footer>
        <div className="d-flex justify-content-between w-100">
          <div>
            <small className="text-muted">
              {code.split('\n').length} lines • {code.length} characters
            </small>
          </div>
          <div>
            <Button variant="secondary" onClick={onClose} className="me-2">
              Cancel
            </Button>
            <Button variant="outline-primary" onClick={handleTest} className="me-2" disabled={errors.length > 0}>
              <FaPlay className="me-1" />
              Test Code
            </Button>
            <Button variant="primary" onClick={handleSave} disabled={isSaving || errors.length > 0}>
              {isSaving ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Saving...
                </>
              ) : (
                <>
                  <FaSave className="me-1" />
                  Save
                </>
              )}
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default SupervisorCodeEditor;
